import Big from 'big.js';

import { mask, replaceSymbols } from 'app/utils/StringUtils';

const DEFAULT_FRACTION_DIGITS = 2;

/**
 * Возвращает строку с добавленными разделителями
 *
 * Каждые три разряда в целой части, начиная с младшего, разделяются пробелом. Пример: 23 968.
 * @param value
 */
const getWithSpaceDividers = (value: string): string => {
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

/**
 * Возвращает массив из целой и дробной частей числа
 * @param value
 * @param fractionDigits
 */
export const getDecimalParts = (
    value: Big | number,
    fractionDigits?: number,
    forceFractionEnding?: boolean,
): string[] => {
    const fixed = value.toFixed(fractionDigits ?? DEFAULT_FRACTION_DIGITS);

    const parts = parseFloat(fixed).toString().split('.');

    if (forceFractionEnding && parts[1] && parts[1].length < fractionDigits) {
        const missingDigitsCount = fractionDigits - parts[1].length;
        const missingEnding = new Array(missingDigitsCount).fill('0');

        return [parts[0], `${parts[1]}${missingEnding}`];
    }

    return parts;
};

export class FormatUtils {
    static readonly DEFAULT_DATE_FORMAT = 'DD.MM.YYYY';

    /**
     * Формат отображения номера телефона оператора Республики Беларусь
     * Х - любая цифра или "+"
     */
    static readonly BELARUS_PHONE_MASK = 'XXXX XX XXX-XX-XX';

    /**
     * Формат отображения маскированного номера телефона оператора Республики Беларусь
     */
    static readonly MASKED_PHONE_NUMBER =
        FormatUtils.BELARUS_PHONE_MASK.replace(/-/g, ' ');

    /**
     * Возвращает замаскированный номер телефона
     * @param value номер телефона оператора Республики Беларусь
     */
    static toMaskedPhoneNumberString = (value: string): string => {
        const masked = replaceSymbols(value, 7, 10, '•');

        return mask(masked, FormatUtils.MASKED_PHONE_NUMBER);
    };

    /**
     * Преобразует число к строке процентного формата
     *
     * Разделитель – запятая.
     * Максимальное количество отображаемых знаков в дробной части – 2.
     * В дробной части скрываются незначащие нули.
     * После значения всегда отображается символ «%».
     * Дробная часть округляется по правилам:
     * 55.59n отображается как 55,59, если n < 5;
     * 55.59n отображается как 55,6, если n >= 5.
     * @param value
     */
    static toPercentString = (value: Big | number): string => {
        return parseFloat(value.toFixed(2)).toString().replace('.', ',');
    };

    /**
     * Преобразует число к строке целого числа
     *
     * Отображаются только цифры до разделителя без округления.
     * Каждые три разряда в целой части, начиная с младшего, разделяются пробелом. Пример: 23 968.
     * @param value
     */
    static toIntegerString = (value: Big | number): string => {
        const integerPart = getDecimalParts(value)[0].toString();

        return getWithSpaceDividers(integerPart);
    };

    /**
     * Преобразует число к строке дробного числа
     *
     * Разделитель – запятая.
     * Максимальное кол-во отображаемых знаков в дробной части – определяется по тексту спецификации.
     * Каждые три разряда в целой части, начиная с младшего, разделяются пробелом. Пример: 23 968.
     * Незначащие 0 в целой и дробной части скрываются.
     * Дробная часть округляется по правилам:
     * 55.59n отображается как 55,59, если n < 5;
     * 55.59n отображается как 55,6, если n >= 5.
     * @param value
     * @param fractionDigits
     */
    static toDecimalString = (
        value: Big | number,
        fractionDigits?: number,
    ): string => {
        const parts = getDecimalParts(value, fractionDigits);
        parts[0] = getWithSpaceDividers(parts[0]);
        return parts.join(',');
    };

    static _reservedAddressNames = [
        'область',
        'город',
        'проспект',
        'улица',
        'дом',
        'квартира',
    ];

    /**
     * Преобразует строку адреса к виду с заглавной большой буквой
     * Зарезервированные слова, обозначающие тип населенного пункта или области, преобразуются к нижнему регистру
     * @param address
     */
    static toAddressString = (address: string): string => {
        return address
            .split(' ')
            .map((name) => {
                if (
                    FormatUtils._reservedAddressNames.includes(
                        name.toLowerCase(),
                    )
                ) {
                    return name.toLowerCase();
                }

                return name[0].toUpperCase() + name.slice(1).toLowerCase();
            })
            .join(' ');
    };

    /**
     * Обьединяет пользовательскую и доменную части e-mail, обрезая пользовательскую по первому символу "@"
     * @param email строка адреса до символа "@"
     * @param domain строка домена адреса электронной почты
     * @returns целый адрес электронной почты
     */
    static toEmailString = (email: string, domain: string) => {
        const atIndex = email.indexOf('@');
        const notFoundIndex = -1;

        if (atIndex !== notFoundIndex) {
            return email.slice(0, atIndex).concat('@').concat(domain);
        } else {
            return email.concat('@').concat(domain);
        }
    };

    /**
     * Строки соответствия расположения кириллических и латинских символов на клавишах клавиатуры
     */
    static CYRILLIC_KEYBOARD =
        'йцукенгшщзхъфывапролджэячсмитьбюЙЦУКЕНГШЩЗХЪФЫВАПРОЛДЖЭЯЧСМИТЬБЮёЁ"';
    static LATIN_KEYBOARD =
        "qwertyuiop[]asdfghjkl;'zxcvbnm,.QWERTYUIOP{}ASDFGHJKL;'ZXCVBNM,.`~@";

    /**
     * Перевод кириллических символов в латинские, согласно их расположению на клавиатуре
     * @param cyrillic строка, содержащая кириллические и/или латинские символы
     * @returns строку, содержащую исходные латинские символы и замененные на латинские кириллические, сохраняя их позиции
     */
    static cyrillicToLatin = (cyrillic: string) => {
        return [...cyrillic]
            .map((symbol) => {
                const cyrillicIndex =
                    FormatUtils.CYRILLIC_KEYBOARD.indexOf(symbol);
                const notFoundIndex = -1;

                if (cyrillicIndex !== notFoundIndex) {
                    return FormatUtils.LATIN_KEYBOARD[cyrillicIndex];
                } else {
                    return symbol;
                }
            })
            .join('');
    };
}
